import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// Import icons from lucide-react (assuming this is installed)
import { User, ShoppingBag, Menu, X, Shield } from 'lucide-react'; 
// Import SVG paths for the complex logo image
import svgPaths from "../pages/svg-oi7gdwiy0k"; 
import LoginModal from "../components/LoginModal";


export const AppBanner = () => {
  const { user, logout } = useAuth(); 
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // New state for mobile menu
  const [showLogin, setShowLogin] = useState(false);
  
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
    navigate('/'); 
  };

  const menuItems = [
    // { name: 'My Events', path: '/events' },
    // { name: 'My Invitations', path: '/invitations' },
    // { name: 'My Guests', path: '/guests' },
  ];

  const handleLoginSuccess = () => {
    // navigate(`/editor/${selectedTemplateId}`, {state: {mode: "event"}});
    setShowLogin(false);
    setMobileMenuOpen(false);
    navigate("/");
  };

  return (
    // Header container is sticky and controls the overall shadow
    <header className="sticky top-0 z-50 shadow-md">
      
      {/* 1. Top Section: Logo and Profile (Styled like TemplatesPage Navbar) */}
      <div className="bg-white">
      <div className="bg-[#ff0099] text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 text-sm sm:text-base">
          <p className="font-['Poppins'] font-semibold">Create in 10 minutes</p>
          <p className="font-['Poppins'] font-medium text-center sm:text-right">India's Most Loved Invitation Platform</p>
        </div>
      </div>

        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="relative">
                {/* SVG icon part */}
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" viewBox="0 0 25 25">
                  <g>
                    {/* Assuming p16536200 is the gray part and p15940e80 is the pink part */}
                    <path d={svgPaths.p16536200} fill="#333333" />
                    <path d={svgPaths.p15940e80} fill="#FF0099" />
                  </g>
                </svg>
              </div>
              {/* Logo Text with Underline */}
              <div className="relative hidden sm:block">
                <svg className="absolute -top-1" width="124" height="37" fill="none" viewBox="0 0 124 37">
                  <path d={svgPaths.p37ee5430} stroke="#FF0099" strokeWidth="2.20093" />
                </svg>
                <p className="font-['Kaushan_Script'] text-[#ff0099] text-lg sm:text-xl relative">Lagn patrika</p>
              </div>
            </Link>

            {/* Desktop Actions (Hidden on mobile) */}
            <div className="hidden md:flex items-center gap-6">
              
              {/* Always visible: Your Events Button */}
              <Link to="/dashboard" className="flex items-center gap-2 text-[#666666] hover:text-[#ff0099] transition-colors">
                <ShoppingBag className="w-6 h-6" />
                <span className="font-['Poppins']">Dashboard</span>
              </Link>

              {user?.is_admin ? (
                <div className="relative group">
                  <Link to="/admin/templates" className="flex items-center gap-2 text-[#666666] hover:text-[#ff0099] transition-colors">
                    <Shield className="w-6 h-6" />
                    <span className="font-['Poppins']">Admin</span>
                  </Link>
                  <div className="invisible absolute right-0 top-full z-20 min-w-[190px] rounded-xl border border-gray-100 bg-white py-2 shadow-lg opacity-0 transition-all group-hover:visible group-hover:opacity-100">
                    <Link to="/admin/templates" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#ff0099]">
                      Template Manager
                    </Link>
                    <Link to="/admin/templates/ai-create" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#ff0099]">
                      AI Template Creation
                    </Link>
                    <Link to="/admin/assets" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#ff0099]">
                      Asset Library
                    </Link>
                  </div>
                </div>
              ) : null}

              <div className="w-px h-8 bg-[#d9d9d9]" />

              {/* Conditional: Login or Profile Dropdown */}
              {user ? (
                // User Logged In: Profile Dropdown
                <div className="relative">
                  <button 
                    className="flex items-center gap-2 text-[#666666] hover:text-[#ff0099] transition-colors"
                    onClick={() => setIsDropdownOpen(prev => !prev)}
                    aria-expanded={isDropdownOpen}
                  >
                    {/* Use profile image or icon */}
                    {user?.profileImage ? (
                      <img src={user.profileImage} alt="User Profile" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <User className="w-6 h-6" />
                    )}
                    <span className="font-['Poppins']">Profile</span>
                  </button>
                        
                  {isDropdownOpen && (
                    <div className="absolute top-full right-0 z-10 bg-white min-w-[150px] shadow-lg rounded-md mt-2 py-1 border border-gray-100">
                      
                      {/* 1. Edit Profile Button */}
                      <button 
                          onClick={() => {
                              // Assume 'navigate' is available via useNavigate()
                              navigate("/edit-profile"); 
                              setIsDropdownOpen(false);
                          }}
                          // Ensure button takes full width and text aligns left
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" 
                      >
                          Edit Profile
                      </button>

                      {/* 2. Settings Button */}
                      <button 
                          onClick={() => {
                              // Assume 'navigate' is available via useNavigate()
                              navigate("/settings");
                              setIsDropdownOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                          Settings
                      </button>

                      {/* Visual Separator */}
                      <div className="my-1 border-t border-gray-200"></div>

                      {/* 3. Logout Button (Already a button, fixed classes) */}
                      <button 
                          onClick={handleLogout} 
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                          Logout
                      </button>
                    </div>
                )}
                </div>  
              ) : (
                // User Logged Out: Login Button and Modal
                <>
                  {/* 💡 FIX: Use onClick to open the modal, as the Link component is not meant for modals. */}
                  <Link 
                    to="#" // Use '#' or the default home path if you don't want navigation
                    onClick={(e) => {
                      e.preventDefault(); // Prevent default link behavior
                      setShowLogin(true);
                    }} 
                    className="flex items-center gap-2 text-[#666666] hover:text-[#ff0099] transition-colors"
                  >
                    <User className="w-6 h-6" />
                    <span className="font-['Poppins']">Login</span>
                  </Link>
                  
                  {showLogin && (
                    <LoginModal
                      // The defaultValue prop seems unusual here, check its usage in LoginModal
                      defaultValue={""} 
                      onClose={() => setShowLogin(false)}
                      onSuccess={handleLoginSuccess}
                    />
                  )}
                </>
              )}
            </div>

            {/* Mobile Menu Button (Visible on mobile) */}
            <button 
              onClick={() => setMobileMenuOpen(prev => !prev)}
              className="md:hidden p-2 text-[#ff0099]"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu Dropdown (Below Navbar) */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 bg-white px-4 py-3 space-y-3">
                
                {/* Your Events (Mobile) */}
                <Link to="/dashboard" className="flex items-center gap-3 text-[#666666] w-full py-2">
                    <ShoppingBag className="w-5 h-5" />
                    <span className="font-['Poppins']">Dashboard</span>
                </Link>

                {user?.is_admin ? (
                  <>
                    <Link to="/admin/templates" className="flex items-center gap-3 text-[#666666] w-full py-2">
                        <Shield className="w-5 h-5" />
                        <span className="font-['Poppins']">Admin Templates</span>
                    </Link>
                    <Link to="/admin/templates/ai-create" className="flex items-center gap-3 text-[#666666] w-full py-2 pl-8">
                        <span className="font-['Poppins']">AI Template Creation</span>
                    </Link>
                    <Link to="/admin/assets" className="flex items-center gap-3 text-[#666666] w-full py-2 pl-8">
                        <span className="font-['Poppins']">Asset Library</span>
                    </Link>
                  </>
                ) : null}

                {/* Conditional Login/Logout (Mobile) */}
                {user ? (
                    <button onClick={handleLogout} className="flex items-center gap-3 text-[#666666] w-full py-2">
                        <User className="w-5 h-5" />
                        <span className="font-['Poppins']">Logout</span>
                    </button>
                ) : (
                    <Link to="/login" className="flex items-center gap-3 text-[#666666] w-full py-2">
                        <User className="w-5 h-5" />
                        <span className="font-['Poppins']">Login</span>
                    </Link>
                )}
            </div>
          )}
        </div>
      </div>

      {/* 2. Bottom Section: Main Navigation Menu (Preserved from original AppBanner) */}
      <nav className="bg-[#ff0099] border-t border-white/20 py-2">
        <ul className="flex justify-center list-none m-0 p-0 gap-8 max-w-7xl mx-auto px-4">
          {menuItems.map((item) => (
            <li key={item.path}>
              <Link 
                to={item.path} 
                className="text-white py-2 px-1 transition-all hover:text-[#ffeb3b] border-b-2 border-transparent hover:border-[#ffeb3b] font-['Poppins'] text-sm font-medium hidden sm:block"
              >
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
};
